import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import Pagination from "../../components/Panigation";
import moment from "moment";
import { showToast, showError } from "../../constants/toasts";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { useGetLoyaltyPointsQuery, useDeleteLoyaltyPointMutation, useUpdateLoyaltySettingsMutation } from "../../store/api/loyaltyAPI";
import { LOYALTY_TYPE } from "../../constants/enums";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`loyalty-tabpanel-${index}`}
      aria-labelledby={`loyalty-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const LoyaltyPoints = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [tabIndex, setTabIndex] = useState(0);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useGetLoyaltyPointsQuery({
    page: page,
    limit: rowsPerPage,
  });

  const [deleteLoyaltyPoint, { isLoading: isDeleting }] = useDeleteLoyaltyPointMutation();

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id: string, name: string) => {
    setItemToDelete({ id, name });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const res = await deleteLoyaltyPoint({ id: itemToDelete.id }).unwrap();
      showToast(res.message || "Rule deleted successfully");
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      console.error("Error deleting rule:", error);
      showError(error?.data?.message || "Failed to delete rule");
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const getLoyaltyTypeLabel = (type: number) => {
    const entry = Object.entries(LOYALTY_TYPE).find(([_, v]) => v === type);
    if (!entry) return "Unknown";
    // Convert SCREAMING_SNAKE_CASE to Title Case
    return entry[0]
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Redemption Criteria State
  const [conversionRate, setConversionRate] = useState("");
  const [minPoints, setMinPoints] = useState("");
  const [maxRedemption, setMaxRedemption] = useState("");
  const [allowCheckout, setAllowCheckout] = useState(true);
  const [restrictProducts, setRestrictProducts] = useState(false);

  const [updateLoyaltySettings, { isLoading: isUpdatingSettings }] = useUpdateLoyaltySettingsMutation();

  const handleSaveSettings = async () => {
    if (!conversionRate || !minPoints || !maxRedemption) {
      showError("Please fill all fields");
      return;
    }

    try {
      const payload = {
        conversionRate: Number(conversionRate),
        minimumPointsForRedemption: Number(minPoints),
        maxRedemptionLimit: Number(maxRedemption),
      };
      const res = await updateLoyaltySettings(payload).unwrap();
      showToast(res.message || "Settings updated successfully");
    } catch (error: any) {
      console.error("Error updating settings:", error);
      showError(error?.data?.message || "Failed to update settings");
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Loyalty Points Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Loyalty Points Management
        </p>
      </div>

      <Box className="custom_tabs1">
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          aria-label="Loyalty Points Tabs"
          className="custom_tabs_links"
        >
          <Tab label="Point-Earning Rules" />
          <Tab label="Redemption Criteria" />
        </Tabs>
      </Box>

      <div className="cards">
        {/* TAB 1: Point-Earning Rules */}
        <TabPanel value={tabIndex} index={0}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search by rule name or trigger..."
              />
            </div>
            <div className="right_s">
              <Button className="btn btn_primary" onClick={() => navigate("add")}>
                <AddIcon /> Add Rule
              </Button>
            </div>
          </div>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No</TableCell>
                  <TableCell>Rule Name</TableCell>
                  <TableCell>Action / Trigger</TableCell>
                  <TableCell>Point Value</TableCell>
                  <TableCell>Min Spend / Threshold</TableCell>
                  <TableCell>Activation Period</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.data?.list
                    .filter(
                      (rule) =>
                        rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        getLoyaltyTypeLabel(rule.type)
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    )
                    .map((rule, index) => (
                      <TableRow hover key={rule._id}>
                        <TableCell>
                          {(page - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>{rule.name}</TableCell>
                        <TableCell>{getLoyaltyTypeLabel(rule.type)}</TableCell>
                        <TableCell>{rule.points}</TableCell>
                        <TableCell>
                          {rule.minimumSpend ? `$${rule.minimumSpend}` : "N/A"}
                        </TableCell>
                        <TableCell>
                          {moment(rule.startDate).format("DD MMM YYYY")} -{" "}
                          {moment(rule.endDate).format("DD MMM YYYY")}
                        </TableCell>
                        <TableCell>
                          <div className="table_actions v2">
                            <IconButton onClick={() => navigate(`edit/${rule._id}`, { state: { rule } })}>
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Edit"
                              />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteClick(rule._id, rule.name)}>
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete"
                              />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data?.data?.pagination?.totalPages && data?.data?.pagination?.totalPages > 0 ? (
            <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
              <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
                Showing <strong>{(page - 1) * rowsPerPage + 1}</strong> to <strong>{Math.min(page * rowsPerPage, data?.data?.pagination?.total || 0)}</strong> of <strong>{data?.data?.pagination?.total || 0}</strong> entries
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Pagination
                  module={data?.data?.list}
                  page={page}
                  onPageChange={handleChangePage}
                  totalPages={data?.data?.pagination?.totalPages}
                  setPage={setPage}
                  totalCount={data?.data?.pagination?.total}
                  limit={rowsPerPage}
                />
              </div>
            </div>
          ) : null}
        </TabPanel>

        {/* TAB 2: Redemption Criteria */}
        <TabPanel value={tabIndex} index={1}>
          <div className="form">
            <div className="gap_p">
              {/* i. Redemption Conversion Rate */}
              <div className="control_group w_33">
                <label>Redemption Conversion Rate</label>
                <TextField
                  placeholder="e.g., 100 points = 1"
                  fullWidth
                  value={conversionRate}
                  onChange={(e) => {
                    // Allow only digits
                    if (/^\d{0,10}$/.test(e.target.value)) {
                      setConversionRate(e.target.value);
                    }
                  }}
                  type="text"
                />
              </div>

              {/* ii. Minimum Points for Redemption */}
              <div className="control_group w_33">
                <label>Minimum Points for Redemption</label>
                <TextField
                  placeholder="Enter minimum points required"
                  fullWidth
                  value={minPoints}
                  onChange={(e) => {
                    if (/^\d{0,10}$/.test(e.target.value)) {
                      setMinPoints(e.target.value);
                    }
                  }}
                  type="text"
                />
              </div>

              {/* iii. Maximum Redemption Limit */}
              <div className="control_group w_33">
                <label>Maximum Redemption Limit</label>
                <TextField
                  placeholder="Enter maximum points per transaction"
                  fullWidth
                  value={maxRedemption}
                  onChange={(e) => {
                    if (/^\d{0,10}$/.test(e.target.value)) {
                      setMaxRedemption(e.target.value);
                    }
                  }}
                  type="text"
                />
              </div>
              <div className="control_group w_100 ">
                <label>Applicable Scenarios</label>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allowCheckout}
                      onChange={(e) => setAllowCheckout(e.target.checked)}
                    />
                  }
                  label="Allow redemption during checkout"
                />


              </div>
            </div>

            {/* iv. Applicable Scenarios */}

            {/* Save Button */}
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                onClick={handleSaveSettings}
                disabled={isUpdatingSettings}
              >
                {isUpdatingSettings ? "Saving..." : "Save Criteria"}
              </Button>
            </div>
          </div>
        </TabPanel>
      </div>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        setOpen={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default LoyaltyPoints;
